/**
 * Offers a reload when the page is running a build we have since replaced.
 *
 * It only ever offers. Reloading under someone mid-RFQ would cost them more than
 * the stale code does, so the choice stays theirs, and declining it lasts for
 * the rest of the session.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { checkForNewerBuild } from "./build-version";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** A floor between checks, so returning to the tab repeatedly costs nothing. */
const MIN_GAP_MS = 60 * 1000;
const DISMISSED_KEY = "newVersionNoticeDismissed";

export const NewVersionNotice = () => {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === "1",
  );
  const lastCheck = useRef(0);

  useEffect(() => {
    let live = true;

    const check = async () => {
      if (Date.now() - lastCheck.current < MIN_GAP_MS) return;
      lastCheck.current = Date.now();
      const newer = await checkForNewerBuild();
      if (live && newer) setStale(true);
    };

    // The first check is deferred: a page still loading has nothing to gain from
    // being told to load again.
    const startTimer = window.setTimeout(() => void check(), 30 * 1000);
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    // Coming back to a tab is exactly when it has most likely fallen behind.
    const onVisibility = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      live = false;
      window.clearTimeout(startTimer);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  if (!stale || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm rounded-lg bg-[#00062e] px-4 py-3 text-white shadow-lg">
      <div className="flex items-start gap-3">
        <div className="flex-1 text-sm">
          <p className="font-medium">A newer version of Kono is available.</p>
          <p className="mt-1 text-white/70">
            This page is running an older one. Reload to pick it up.
          </p>
        </div>
        <button
          onClick={() => {
            sessionStorage.setItem(DISMISSED_KEY, "1");
            setDismissed(true);
          }}
          className="shrink-0 text-white/60 hover:text-white"
          aria-label="Dismiss"
          title="Not now"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-bold text-[#00062e] hover:bg-[#009499] hover:text-white"
      >
        <ArrowPathIcon className="h-4 w-4" />
        Reload
      </button>
    </div>
  );
};
